import { routeHref } from '../../lib/router.js'
import { touched } from '../../lib/live.js'
import { COUNTDOWN_DAYS, VIEWS, addDays, calendarItems, calendarList, calendarPrefs, countdowns, dayLabel, defaultSlot, eventsRequest, loadedFor, rememberCalendar, slotTimes, stepDay, subscriptionsRequest, today, validDay, viewDays, viewTitle, viewWindow } from '../../lib/calendar.js'
import { dayMarks } from '../../lib/planner.js'
import { checkHabit } from '../../lib/habits.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- tpl-calendar ---
let __fez_component_0;
{
// Views with the habits row.
const GRID_VIEWS = ['day', 'week']

const VIEW_KEYS = { d: 'day', w: 'week', m: 'month', y: 'year', a: 'agenda' }
__fez_component_0 = class {
  init() {
    this.state.loading = true
    this.state.error = ''
    this.state.raw = []
    this.state.loaded = null
    this.state.countRaw = []
    this.state.subscriptions = []
    this.state.marks = {}
    this.state.habits = []
    this.state.sideOpen = false
    this.state.seq = 0
    this.state.opened = ''
    this.state.night = Boolean(calendarPrefs().night)
    this.state.query = ''
    this.state.searchOpen = false
  }

  onMount() {
    this.on(window, 'focus', () => this.load(), { throttle: 2000 })
    this.on(document, 'keydown', (event) => this.onKey(event))
    this.subscribe('live:change', (change) => {
      const refs = [...new Set(this.calendars().map((calendar) => calendar.container).filter(Boolean))]
      if (touched(change, refs).length) this.load()
    })
    this.subscribe('live:calendar', () => this.load())
  }

  onGlobalStateChange(key) {
    if (key === 'mailboxes') this.load()
  }

  // The first call loads too: `#/calendar` alone hands the page no props.
  onRefresh(props, changed) {
    let loading = null
    if (!this.state.started || 'view' in changed || 'date' in changed) {
      this.state.started = true
      loading = this.load()
    }
    if ('q' in changed && (props.q || '') !== this.state.query.trim()) this.state.query = props.q || ''
    if ('event' in changed && props.event) this.openLinked(loading)
  }

  // What the URL says, else what this browser last used, else the default for the screen.
  view() {
    const view = this.props.view || calendarPrefs().view
    if (VIEWS.includes(view)) return view
    return window.matchMedia('(max-width: 767px)').matches ? 'agenda' : 'week'
  }

  hidden() {
    const value = this.props.hide ?? calendarPrefs().hide ?? ''
    return String(value).split(',').filter(Boolean)
  }

  calendars() {
    return calendarList(this.globalState.mailboxes, this.hidden(), this.state.subscriptions, this.globalState.mailboxSession ? '' : this.globalState.me?.ref)
  }

  async load() {
    const seq = ++this.state.seq
    const view = this.view()
    const days = viewDays(view, validDay(this.props.date))
    const session = this.globalState.mailboxSession
    const events = eventsRequest(viewWindow(days), view === 'year' ? 'all_day' : undefined)
    // Countdowns reach past the view: an event up to 30 days after its last day counts down in it.
    const counting = view === 'year' ? null : eventsRequest(viewWindow([days[0], addDays(days[days.length - 1], COUNTDOWN_DAYS + 1)]), 'countdown')
    const subscriptions = subscriptionsRequest(session)
    const planner = this.hasPlanner()
    const grid = planner && GRID_VIEWS.includes(view)
    const span = { from: days[0], to: days[days.length - 1] }
    try {
      const [result, counted, subscribed, marked, habits] = await Promise.all([
        window.App.api(events.path, events.params, { silent: true }),
        counting ? window.App.api(counting.path, counting.params, { silent: true }) : { items: [] },
        window.App.api(subscriptions.path, subscriptions.params, { silent: true }),
        planner ? window.App.api('planner/days', span, { silent: true }) : { items: [] },
        grid ? window.App.api('planner/habits', span, { silent: true }) : { items: [] },
      ])
      if (seq !== this.state.seq) return
      this.state.raw = result.items
      // The days `raw` holds every event of (the year asks for all-day ones only).
      this.state.loaded = { from: span.from, to: span.to, timed: view !== 'year' }
      this.state.countRaw = counted.items
      this.state.subscriptions = subscribed.items
      this.state.marks = dayMarks(marked.items)
      this.state.habits = habits.items
      this.state.error = ''
    } catch (error) {
      if (seq === this.state.seq) this.state.error = error.message
    } finally {
      if (seq === this.state.seq) this.state.loading = false
    }
  }

  // Any move leaves a search; only the search box passes `q`.
  go(params) {
    const next = { view: this.props.view, date: this.props.date, hide: this.props.hide, ...params }
    window.location.hash = routeHref('calendar', next)
  }

  setView(view) {
    rememberCalendar({ view })
    this.go({ view })
  }

  step(dir) {
    this.go({ date: stepDay(this.view(), validDay(this.props.date), dir) })
  }

  goToday() {
    this.go({ date: today() })
  }

  pickDate(value) {
    if (value) this.go({ date: value })
  }

  // The calendar button opens the browser's date picker on the hidden date field under it.
  openDate() {
    const input = this.find('.cal-date-input')
    if (!input) return
    try {
      input.showPicker()
    } catch {
      input.focus()
      input.click()
    }
  }

  // A day header, "more" or a day of the year opens that day.
  openDay(day) {
    rememberCalendar({ view: 'day' })
    this.go({ view: 'day', date: day })
  }

  // A month's name or "more" in the year opens the month.
  openMonth(day) {
    rememberCalendar({ view: 'month' })
    this.go({ view: 'month', date: day })
  }

  setNight(open) {
    this.state.night = open
    rememberCalendar({ night: open })
  }

  toggle(ref) {
    const hidden = this.hidden()
    const hide = (hidden.includes(ref) ? hidden.filter((item) => item !== ref) : [...hidden, ref]).join(',')
    rememberCalendar({ hide })
    this.go({ hide })
  }

  toggleSide() {
    this.state.sideOpen = !this.state.sideOpen
  }

  // Search: the words go in the URL after a pause, replacing the entry while typing.
  typed(value) {
    this.state.query = value
    clearTimeout(this.state.searchTimer)
    this.state.searchTimer = this.setTimeout(() => this.searchFor(value), 300)
  }

  searchFor(value) {
    const q = value.trim()
    if (q === (this.props.q || '')) return
    if (!q) return this.go({})
    const href = routeHref('calendar', { view: this.props.view, date: this.props.date, hide: this.props.hide, q })
    if (this.props.q) window.location.replace(href)
    else window.location.hash = href
  }

  searchKey(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      clearTimeout(this.state.searchTimer)
      this.searchFor(event.target.value)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      this.closeSearch()
      event.target.blur()
    }
  }

  closeSearch() {
    clearTimeout(this.state.searchTimer)
    this.state.query = ''
    this.state.searchOpen = false
    if (this.props.q) this.go({})
  }

  async openSearch() {
    if (!this.state.canSearch) return
    this.state.searchOpen = true
    await this.tick()
    const input = this.find('.cal-search-input')
    input?.focus()
    input?.select()
  }

  // A result: its day in this view (the year shows the day), then the event.
  pick(item) {
    const view = this.view() === 'year' ? 'day' : this.view()
    clearTimeout(this.state.searchTimer)
    this.state.query = ''
    this.state.searchOpen = false
    this.go({ view, date: item.firstDay })
    this.showEvent(item.event, item.calendar, item)
  }

  // A to-do opens its week, a day its day and its dialog.
  pickPlanner(row) {
    clearTimeout(this.state.searchTimer)
    this.state.query = ''
    this.state.searchOpen = false
    this.go({ view: row.view, date: row.date })
    if (row.kind === 'day') this.openPlanned(row.date, false)
  }

  // The planner is a person's: a mailbox session has none.
  hasPlanner() {
    return Boolean(this.globalState.me) && !this.globalState.mailboxSession
  }

  // A day's name, circle and journal; `journal` opens straight into writing it.
  async openPlanned(day, journal) {
    if (!this.hasPlanner()) return
    const saved = await window.Dialog.open('calendar-day-dialog', { day, journal }, { title: dayLabel(day, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }), size: 'md' })
    if (!saved) return
    const { [day]: _, ...marks } = this.state.marks
    this.state.marks = saved.updated_at ? { ...marks, [day]: { day, label: saved.label, circled: saved.circled, has_journal: Boolean(saved.journal) } } : marks
  }

  // A habit chip: checked or unchecked at once, then the server's streak (or the list again).
  async checkHabit(ref, day, done) {
    this.state.habits = checkHabit(this.state.habits, ref, day, done)
    try {
      const saved = await window.App.api('planner/habit_check', { habit: ref, day, done }, { silent: true })
      this.state.habits = checkHabit(this.state.habits, ref, day, saved.done, saved.streak)
    } catch (error) {
      window.Toast.error(error.message)
      this.load()
    }
  }

  async manageHabits() {
    this.state.sideOpen = false
    await window.Dialog.open('calendar-habits-dialog', {}, { title: 'Habits', size: 'md' })
    this.load()
  }

  // The day New event and Shift+W take: today when the view shows it, else the day it is on.
  focusDay() {
    return this.state.days.includes(today()) ? today() : this.state.day
  }

  onKey(event) {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return
    if (event.target.closest?.('input, textarea, select, [contenteditable]') || window.Dialog?.isOpen()) return
    const key = event.key
    const actions = {
      t: () => this.goToday(),
      j: () => this.step(1),
      n: () => this.step(1),
      k: () => this.step(-1),
      p: () => this.step(-1),
      c: () => this.state.canCreate && this.newEvent(),
      W: () => this.openPlanned(this.focusDay(), true),
      '/': () => this.openSearch(),
      s: () => this.openSearch(),
      '?': () => window.Dialog.open('mail-keys-dialog', {}, { title: 'Keyboard shortcuts' }),
      Escape: () => this.props.q && this.closeSearch(),
    }
    const action = VIEW_KEYS[key] ? () => this.setView(VIEW_KEYS[key]) : actions[key]
    if (!action) return
    event.preventDefault()
    action()
  }

  // A mailbox's published feed, subscriptions and calendar files.
  async manage(ref) {
    const calendar = this.calendars().find((entry) => entry.ref === ref)
    if (!calendar) return
    this.state.sideOpen = false
    await window.Dialog.open('calendar-feeds-dialog', { container: calendar.container, mailbox: calendar.ref }, { title: `Feeds and files: ${calendar.address}`, size: 'md' })
    this.load()
  }

  // An event of the view, or one a countdown line names.
  async openEvent(key) {
    const item = this.state.items.find((entry) => entry.key === key) || this.state.counting.find((entry) => entry.key === key)
    if (!item) return
    await this.showEvent(item.event, item.calendar, item)
  }

  async showEvent(event, calendar, occurrence) {
    const shown = occurrence ? { start: occurrence.start, end: occurrence.end, all_day: occurrence.all_day, floating: occurrence.floating, tzid: occurrence.tzid, recurring: occurrence.recurring, instance: occurrence.instance } : null
    const result = await window.Dialog.open('calendar-event-dialog', {
      event,
      calendar: calendar || undefined,
      occurrence: shown,
    }, { title: occurrence?.title || 'Event', size: 'md' })
    if (result?.edit) await this.editEvent(result.view, result.edit === 'one' ? shown : null)
    this.load()
    // An edit or a delete changes the hours of the event's labels.
    window.Fez.publish('labels:change')
  }

  async create(day, from, to, allDay) {
    if (!this.state.canCreate) return
    const slot = allDay ? { start: { date: day, time: '' }, end: { date: day, time: '' } } : from === null || from === undefined ? defaultSlot(day) : slotTimes(day, from, to)
    const saved = await window.Dialog.open('calendar-event-form', { start: slot.start, end: slot.end, all_day: Boolean(allDay) }, { title: 'New event', size: 'md' })
    if (saved) this.load()
  }

  // The toolbar's New event: today when the view shows it, else the day the view is on.
  newEvent() {
    this.create(this.focusDay())
  }

  async editEvent(view, occurrence) {
    const one = Boolean(occurrence)
    await window.Dialog.open('calendar-event-form', {
      event: view,
      instance: one ? occurrence.instance : undefined,
      occurrence: one ? occurrence : undefined,
    }, { title: one ? 'Edit this event' : view.recurring ? 'Edit the series' : 'Edit event', size: 'md' })
  }

  // A message's card or Next up links here with the event (Next up also names the `instance`):
  // open it once, as that instance when the loaded window has it.
  async openLinked(loading) {
    const { event, instance, calendar } = this.props
    const key = `${event}:${instance || ''}`
    if (this.state.opened === key) return
    this.state.opened = key
    await loading
    const found = instance ? this.state.raw.find((item) => String(item.instance) === instance && item.event === event) : null
    this.showEvent(event, calendar, found ? { ...found, title: found.summary || '(no title)' } : null)
  }

  beforeRender() {
    const view = this.view()
    const day = validDay(this.props.date)
    const calendars = this.calendars()
    const session = this.globalState.mailboxSession
    this.state.view = view
    this.state.day = day
    this.state.days = viewDays(view, day)
    // Just moved to days not loaded yet: the old events are no guide to what is free there.
    this.state.pending = !loadedFor(this.state.loaded, this.state.days)
    this.state.title = viewTitle(view, day)
    this.state.calendars = calendars
    this.state.several = calendars.length > 1
    this.state.views = VIEWS.map((id) => ({ id, label: id[0].toUpperCase() + id.slice(1), active: id === view }))
    this.state.items = calendarItems(this.state.raw, calendars, session)
    this.state.counting = view === 'year' ? [] : calendarItems(this.state.countRaw, calendars, session)
    this.state.countdowns = countdowns(this.state.counting, this.state.days)
    this.state.empty = !this.state.loading && !this.state.error && !this.state.items.length
    this.state.canCreate = calendars.length > 0
    this.state.canSearch = Boolean(this.globalState.me)
    this.state.planner = this.hasPlanner()
    this.state.shownMarks = this.state.planner ? this.state.marks : null
    this.state.shownHabits = this.state.planner ? this.state.habits : null
    this.state.searching = Boolean(this.props.q)
    this.state.searchShown = this.state.searchOpen || this.state.searching
  }

  CSS = `:fez {
display:block;height:100%;.cal{display:grid;grid-template-columns:var(--sidebar-width) minmax(0,1fr);height:100%;min-height:0}.cal-side{min-height:0;overflow-y:auto;padding:var(--space-4) var(--space-3);border-right:1px solid var(--color-border);background:var(--color-bg-subtle)}.cal-side-title{padding:0 var(--space-2) var(--space-2);color:var(--color-text-subtle);font-size:var(--text-xs);font-weight:var(--weight-bold);letter-spacing:0.06em;text-transform:uppercase}.cal-box{display:flex;align-items:center;gap:var(--space-2);padding:var(--space-2);border-radius:var(--radius-md);cursor:pointer;&:hover{background:var(--color-bg-hover)}input{position:absolute;opacity:0;pointer-events:none}&:has(input:focus-visible){outline:2px solid var(--color-focus);outline-offset:-2px}}.cal-swatch{display:grid;place-items:center;width:18px;height:18px;flex:none;border:2px solid var(--tag);border-radius:var(--radius-sm);color:var(--color-text-inverse)}.cal-box.on .cal-swatch{background:var(--tag)}.cal-box-text{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:var(--text-sm);font-weight:var(--weight-medium);line-height:var(--leading-tight)}.cal-box-local{color:var(--color-text-muted)}.cal-row{display:flex;align-items:center;gap:var(--space-1);.cal-box{flex:1;min-width:0}}.cal-manage{flex:none;opacity:0.6}.cal-row:hover .cal-manage,.cal-manage:focus-within{opacity:1}.cal-sub{padding-left:var(--space-6)}.cal-sub-error{display:inline-flex;margin-left:auto;color:var(--color-warning)}.cal-side-note{margin-top:var(--space-4);padding:0 var(--space-2);color:var(--color-text-muted);font-size:var(--text-xs)}.cal-side-gap{margin-top:var(--space-6)}.cal-link{display:flex;align-items:center;gap:var(--space-2);width:100%;padding:var(--space-2);border:0;border-radius:var(--radius-md);background:transparent;color:var(--color-text-muted);font:inherit;text-align:left;text-decoration:none;cursor:pointer;&:hover{background:var(--color-bg-hover);color:var(--color-text)}&:focus-visible{outline:2px solid var(--color-focus);outline-offset:-2px}.cal-box-text{color:var(--color-text)}}.cal-main{display:flex;flex-direction:column;min-width:0;min-height:0;background:var(--color-bg-raised)}.cal-bar{display:flex;flex-wrap:wrap;align-items:center;gap:var(--space-2);min-height:var(--header-height);padding:var(--space-2) var(--space-4);border-bottom:1px solid var(--color-border);container:cal-bar / inline-size}.cal-new,.cal-date{display:inline-flex}.cal-new-narrow{display:none}.cal-menu{display:none}.cal-title{flex:1;min-width:0;margin-left:var(--space-2);overflow:hidden;font-size:var(--text-lg);letter-spacing:-0.01em;text-overflow:ellipsis;white-space:nowrap}.cal-date{position:relative}.cal-date-input{position:absolute;inset:0;width:100%;height:100%;padding:0;border:0;opacity:0;pointer-events:none}.cal-search-toggle{display:none}.cal-search{position:relative;display:flex;align-items:center;width:200px}.cal-search-icon{position:absolute;left:var(--space-2);display:flex;color:var(--color-text-subtle);pointer-events:none}.cal-search-input{width:100%;height:28px;padding:0 var(--space-2) 0 calc(var(--space-2) + 20px);border:1px solid var(--color-border);border-radius:var(--radius-md);background:var(--color-bg-raised);color:var(--color-text);font:inherit;font-size:var(--text-sm);&::placeholder{color:var(--color-text-subtle)}&:focus{border-color:var(--color-accent);outline:2px solid var(--color-focus);outline-offset:-1px}}.cal-views{display:inline-flex;box-sizing:border-box;height:28px;padding:2px;border:1px solid var(--color-border);border-radius:var(--radius-md);background:var(--color-bg-subtle)}.cal-view{padding:0 var(--space-3);border:0;border-radius:var(--radius-sm);background:transparent;color:var(--color-text-muted);font-size:var(--text-sm);font-weight:var(--weight-medium);line-height:1;cursor:pointer;&:hover{color:var(--color-text)}&.active{background:var(--color-bg-raised);color:var(--color-text);box-shadow:var(--shadow-sm)}}.cal-body{position:relative;flex:1;min-height:0;overflow:hidden}.cal-stack{display:flex;flex-direction:column;height:100%}.cal-fill{flex:1;min-height:0}.cal-note{display:grid;place-items:center;height:100%;padding:var(--space-6)}.cal-scrim{display:none}@container cal-bar (max-width:1169px){.cal-search-toggle{display:inline-flex}.cal-search{display:none;order:1;flex-basis:100%;width:auto;&.open{display:flex}}}@container cal-bar (max-width:959px){.cal-new-wide{display:none}.cal-new-narrow{display:inline-flex}.cal-title{margin-left:0}.cal-view{padding:0 var(--space-2)}}@container cal-bar (max-width:939px){.cal-title{font-size:var(--text-md)}}@container cal-bar (max-width:819px){.cal-title{order:-1;flex-basis:100%;margin:0 0 var(--space-1)}}@media (max-width:1099px){.cal{grid-template-columns:minmax(0,1fr)}.cal-menu{display:inline-flex}.cal-side{position:fixed;top:0;bottom:0;left:var(--rail-width);z-index:var(--z-dropdown);width:var(--sidebar-width);box-shadow:var(--shadow-lg);transform:translateX(calc(-100% - var(--rail-width)));visibility:hidden;transition:transform var(--duration-normal) var(--ease),visibility var(--duration-normal)}.cal-side-open .cal-side{transform:none;visibility:visible}.cal-side-open .cal-scrim{display:block;position:fixed;inset:0 0 0 var(--rail-width);z-index:calc(var(--z-dropdown) - 1);padding:0;border:0;background:var(--color-overlay);cursor:default}} :mobile {.cal-bar{padding:var(--space-2) var(--space-3)}.cal-date{display:none}.cal-side{left:0;bottom:calc(40px + 2 * var(--space-2));width:min(var(--sidebar-width),86vw);transform:translateX(-100%)}.cal-side-open .cal-scrim{inset:0 0 calc(40px + 2 * var(--space-2)) 0}}
}`

  HTML = `<div class="cal" class:cal-side-open={state.sideOpen}>
<aside class="cal-side" aria-label="Calendars">
<div class="cal-side-title">Calendars</div>
{#each state.calendars as calendar}
<div class="cal-row" key={calendar.ref}>
<label class="cal-box" class:on={calendar.shown}>
<input type="checkbox" checked={calendar.shown} onchange="fez.toggle('{calendar.ref}')">
<span class="cal-swatch" style="--tag: var(--color-avatar-{calendar.color})">{#if calendar.shown}<ui-icon name="check" size="12"></ui-icon>{/if}</span>
<span class="cal-box-text">{#if calendar.kind === 'personal'}{calendar.name}{:else if calendar.domain}<span class="cal-box-local">{calendar.prefix}@</span>{calendar.domain}{:else}{calendar.prefix}{/if}</span>
</label>
{#if calendar.kind === 'mailbox'}<span class="cal-manage"><ui-button variant="ghost" size="sm" icon="gear-six" title="Feeds and files" aria-label="Feeds and files" onclick="fez.manage('{calendar.ref}')"></ui-button></span>{/if}
</div>
{#each calendar.subscriptions as subscription}
<label class="cal-box cal-sub" class:on={subscription.shown} key={subscription.ref} title={subscription.error || subscription.url}>
<input type="checkbox" checked={subscription.shown} onchange="fez.toggle('{subscription.ref}')">
<span class="cal-swatch" style="--tag: var(--color-avatar-{subscription.color})">{#if subscription.shown}<ui-icon name="check" size="12"></ui-icon>{/if}</span>
<span class="cal-box-text">{subscription.name}</span>
{#if subscription.error}<span class="cal-sub-error"><ui-icon name="warning-circle" size="14"></ui-icon></span>{/if}
</label>
{/each}
{:else}
<p class="cal-side-note">You read no mailboxes yet.</p>
{/each}
<p class="cal-side-note">Invites, updates and cancellations that reach these mailboxes keep this calendar current. Click an empty time, or drag across it, to add an event of your own: with nobody invited it goes in My calendar. The gear publishes a mailbox's calendar, subscribes it to others, and imports or downloads <code>.ics</code> files.</p>
{#if state.planner}
<div class="cal-side-title cal-side-gap">Planner</div>
<button type="button" class="cal-link" onclick="fez.manageHabits()"><ui-icon name="sliders-horizontal" size="16"></ui-icon><span class="cal-box-text">Habits</span></button>
{/if}
</aside>
<button class="cal-scrim" type="button" aria-label="Close calendars" onclick="fez.toggleSide()"></button>
<section class="cal-main">
<header class="cal-bar">
<span class="cal-menu"><ui-button variant="ghost" size="sm" icon="list" title="Calendars" aria-label="Calendars" onclick="fez.toggleSide()"></ui-button></span>
<span class="cal-new cal-new-wide"><ui-button size="sm" variant="primary" icon="plus" label="New event" disabled={!state.canCreate} onclick="fez.newEvent()"></ui-button></span>
<span class="cal-new cal-new-narrow"><ui-button size="sm" variant="primary" icon="plus" title="New event" aria-label="New event" disabled={!state.canCreate} onclick="fez.newEvent()"></ui-button></span>
<ui-button size="sm" label="Today" onclick="fez.goToday()"></ui-button>
<ui-button size="sm" variant="ghost" icon="caret-left" title="Back" aria-label="Back" onclick="fez.step(-1)"></ui-button>
<ui-button size="sm" variant="ghost" icon="caret-right" title="Forward" aria-label="Forward" onclick="fez.step(1)"></ui-button>
<h1 class="cal-title">{state.title}</h1>
{#if state.canSearch}
<span class="cal-search-toggle"><ui-button variant="ghost" size="sm" icon="magnifying-glass" title="Search the calendar" aria-label="Search the calendar" onclick="fez.openSearch()"></ui-button></span>
<label class="cal-search" class:open={state.searchShown}>
<span class="cal-search-icon"><ui-icon name="magnifying-glass" size="14"></ui-icon></span>
<input class="cal-search-input" type="search" value={state.query} placeholder="Search the calendar" aria-label="Search the calendar" autocomplete="off" oninput="fez.typed(this.value)" onkeydown="fez.searchKey(event)">
</label>
{/if}
<span class="cal-date">
<ui-button variant="ghost" size="sm" icon="calendar-dots" title="Go to date" aria-label="Go to date" onclick="fez.openDate()"></ui-button>
<input class="cal-date-input" type="date" value={state.day} tabindex="-1" aria-hidden="true" onchange="fez.pickDate(this.value)">
</span>
<div class="cal-views" role="tablist">
{#each state.views as view}
<button type="button" class="cal-view" class:active={view.active} role="tab" aria-selected={view.active} onclick="fez.setView('{view.id}')">{view.label}</button>
{/each}
</div>
</header>
<calendar-labels></calendar-labels>
<div class="cal-body">
{#if state.searching}
<calendar-search q={props.q} :calendars="state.calendars" :on_pick="(item) => fez.pick(item)" :on_planner="(row) => fez.pickPlanner(row)"></calendar-search>
{:else if state.error}
<div class="cal-note"><ui-empty icon="warning-circle" title="Could not load the calendar" text={state.error}></ui-empty></div>
{:else if state.loading}
<div class="cal-note"><ui-spinner size="lg"></ui-spinner></div>
{:else if state.view === 'year'}
<calendar-year day={state.day} :items="state.items" :marks="state.shownMarks" :on_day="(day) => fez.openDay(day)" :on_month="(day) => fez.openMonth(day)" :on_open="(key) => fez.openEvent(key)"></calendar-year>
{:else if state.view === 'month'}
<calendar-month :days="state.days" day={state.day} :items="state.items" :countdowns="state.countdowns" :marks="state.shownMarks" :on_open="(key) => fez.openEvent(key)" :on_day="(day) => fez.openDay(day)" :on_create="(day) => fez.create(day)"></calendar-month>
{:else if state.view === 'agenda'}
<calendar-agenda :days="state.days" :items="state.items" :countdowns="state.countdowns" :on_open="(key) => fez.openEvent(key)"></calendar-agenda>
{:else}
<div class="cal-stack">
{#if state.planner}
<calendar-todos day={state.day}></calendar-todos>
{/if}
<div class="cal-fill">
<calendar-week :days="state.days" :items="state.items" :countdowns="state.countdowns" :marks="state.shownMarks" :habits="state.shownHabits" :night="state.night" :pending="state.pending" :on_open="(key) => fez.openEvent(key)" :on_day="(day) => fez.openDay(day)" :on_plan="(day) => fez.openPlanned(day, false)" :on_create="(day, from, to, allDay) => fez.create(day, from, to, allDay)" :on_night="(open) => fez.setNight(open)" :on_habit="(ref, day, done) => fez.checkHabit(ref, day, done)" :on_habits="() => fez.manageHabits()"></calendar-week>
</div>
</div>
{/if}
</div>
</section>
</div>`
};
}

Fez('tpl-calendar', __fez_component_0);

export default __fez_component_0;
